/**
 * Настройки инструментов Фибоначчи.
 *
 * Уровни описаны данными, а не зашиты в рисовальщик: их включают,
 * выключают, перекрашивают и дописывают свои. Рисовальщик берёт то, что
 * лежит в фигуре, и ничего не знает про 0.618.
 *
 * По умолчанию — классический набор: 0, 0.236, 0.382, 0.5, 0.618, 0.786
 * и 1. Продолжения (1.272, 1.414, 1.618, 2.618, 4.236) в списке есть, но
 * выключены: они нужны не всем и не всегда, а сетка из тринадцати линий
 * закрывает график целиком.
 */

import type { DrawingKind } from './kinds.js';

export interface FibLevel {
  readonly value: number;
  readonly enabled: boolean;
  readonly color: string;
}

/** Куда продолжать линии уровней за пределы построения. */
export type FibExtend = 'none' | 'left' | 'right' | 'both';

/** Что писать у линии уровня. */
export type FibLabel = 'values' | 'percents' | 'both' | 'none';

export interface FibOptions {
  readonly levels: readonly FibLevel[];
  readonly extend: FibExtend;
  /** Линия построения — та, по которой натянут инструмент. */
  readonly trendLine: boolean;
  /** Заливка между соседними уровнями. */
  readonly background: boolean;
  /** Плотность заливки, 0…1. */
  readonly backgroundOpacity: number;
  /** Отсчёт от второй точки к первой, а не наоборот. */
  readonly reverse: boolean;
  /**
   * Отсчёт как в привычных терминалах: 0 — там, где отпустили (вторая
   * точка), 1 — там, где нажали (первая), продолжения 1.414 и 1.618 —
   * за первой точкой.
   *
   * Тянут от вершины вниз — сетка откатов стоит от дна, а цели
   * продолжения уходят вверх, за вершину: ровно то, что ждёт трейдер.
   * Так рисуются все новые сетки; нарисованные раньше — без этой
   * отметки — считаются по-старому, чтобы их уровни не перевернулись.
   */
  readonly fromEnd?: boolean;
  /** Показывать цену уровня. */
  readonly showPrices: boolean;
  readonly labels: FibLabel;
  /** С какой стороны стоят подписи. */
  readonly labelSide: 'left' | 'right';
  readonly fontSize: number;
}

/**
 * Цвета классической сетки.
 *
 * Крайние уровни серые — это границы построения, а не сигнал. Золотое
 * сечение выделено фирменным тоном: к нему обращаются чаще всего.
 */
/** Оранжевый, которым золотое сечение выделялось до фирменного спектра. */
const LEGACY_GOLDEN = '#E3A455';

const COLORS = {
  edge: '#79818F',
  shallow: '#5AC8D8',
  middle: '#3FBF87',
  golden: '#6E8BFF',
  deep: '#F0616F',
  extension: '#9B8CE0',
} as const;

const level = (value: number, color: string, enabled = true): FibLevel => ({
  value,
  enabled,
  color,
});

/** Классическая сетка коррекции. */
const RETRACEMENT: readonly FibLevel[] = [
  level(0, COLORS.edge),
  level(0.236, COLORS.shallow),
  level(0.382, COLORS.middle),
  level(0.5, COLORS.middle),
  level(0.618, COLORS.golden),
  level(0.786, COLORS.deep),
  level(1, COLORS.edge),
  level(1.272, COLORS.extension, false),
  level(1.414, COLORS.extension, false),
  level(1.618, COLORS.extension, false),
  level(2.618, COLORS.extension, false),
  level(4.236, COLORS.extension, false),
];

/** Расширение по тренду: смысл в уровнях за единицей, они и включены. */
const EXTENSION: readonly FibLevel[] = [
  level(0, COLORS.edge),
  level(0.618, COLORS.golden),
  level(1, COLORS.edge),
  level(1.618, COLORS.golden),
  level(2.618, COLORS.deep),
  level(4.236, COLORS.extension, false),
];

/** Веер: наклоны, а не горизонтали. */
const FAN: readonly FibLevel[] = [
  level(0.382, COLORS.middle),
  level(0.5, COLORS.middle),
  level(0.618, COLORS.golden),
];

/** Периоды времени: числа последовательности, а не доли. */
const TIME: readonly FibLevel[] = [
  level(1, COLORS.edge),
  level(2, COLORS.edge),
  level(3, COLORS.shallow),
  level(5, COLORS.shallow),
  level(8, COLORS.middle),
  level(13, COLORS.middle),
  level(21, COLORS.golden),
  level(34, COLORS.golden),
  level(55, COLORS.deep),
  level(89, COLORS.deep),
];

const BASE: Omit<FibOptions, 'levels'> = {
  extend: 'none',
  trendLine: true,
  background: true,
  backgroundOpacity: 0.08,
  reverse: false,
  showPrices: true,
  labels: 'values',
  labelSide: 'right',
  fontSize: 11,
};

const DEFAULTS: Partial<Record<DrawingKind, FibOptions>> = {
  'fib-retracement': { ...BASE, levels: RETRACEMENT },
  'fib-extension': { ...BASE, levels: EXTENSION },
  'fib-fan': { ...BASE, levels: FAN, background: false },
  'fib-time': { ...BASE, levels: TIME, background: false, showPrices: false, labels: 'values' },
};

/** Есть ли у инструмента настройки уровней. */
export function hasFibOptions(kind: DrawingKind): boolean {
  return kind in DEFAULTS;
}

/** Настройки по умолчанию для инструмента. */
export function defaultFibOptions(kind: DrawingKind): FibOptions | null {
  const preset = DEFAULTS[kind];
  return preset ? { ...preset, levels: preset.levels.map((entry) => ({ ...entry })) } : null;
}

/**
 * Настройки фигуры: сохранённые или по умолчанию.
 *
 * Профиль живёт дольше кода: у фигуры, нарисованной прошлой версией,
 * настроек нет вовсе, и брать их неоткуда, кроме умолчаний.
 */
export function fibOptionsOf(kind: DrawingKind, stored: FibOptions | undefined): FibOptions | null {
  const defaults = defaultFibOptions(kind);
  if (!defaults) return null;
  if (!stored) return defaults;
  return { ...defaults, ...stored, levels: stored.levels ?? defaults.levels };
}

/**
 * Цены, от которой и до которой считаются доли: уровень v стоит на
 * `from + (to − from) · v`.
 */
export function fibSpan(options: FibOptions, first: number, second: number): [number, number] {
  const [from, to] = options.fromEnd === true ? [second, first] : [first, second];
  return options.reverse ? [to, from] : [from, to];
}

/** Только включённые уровни, в порядке возрастания. */
export function activeLevels(options: FibOptions): FibLevel[] {
  return options.levels.filter((entry) => entry.enabled).sort((a, b) => a.value - b.value);
}

/** Подпись уровня: доля, цена или и то и другое. */
export function levelLabel(
  options: FibOptions,
  value: number,
  price: number,
  priceText: (value: number) => string,
): string {
  const parts: string[] = [];

  if (options.labels === 'values' || options.labels === 'both') parts.push(value.toFixed(3));
  if (options.labels === 'percents' || options.labels === 'both') {
    parts.push(`${(value * 100).toFixed(1)}%`);
  }
  if (options.showPrices) parts.push(priceText(price));

  return parts.join(' · ');
}

/** Приведение сохранённых настроек к текущей форме. */
export function normalizeFibOptions(value: unknown): FibOptions | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Partial<FibOptions>;

  const levels = Array.isArray(raw.levels)
    ? raw.levels
        .filter(
          (entry): entry is FibLevel =>
            typeof entry === 'object' && entry !== null && Number.isFinite(entry.value),
        )
        .map((entry) => ({
          value: entry.value,
          enabled: entry.enabled !== false,
          // Прежний оранжевый акцент золотого сечения — остаток старой
          // темы, а не выбор человека: заменяем на фирменный тон.
          color:
            typeof entry.color === 'string'
              ? entry.color === LEGACY_GOLDEN
                ? COLORS.golden
                : entry.color
              : COLORS.edge,
        }))
    : undefined;

  return {
    ...BASE,
    ...raw,
    ...(levels ? { levels } : { levels: RETRACEMENT }),
  };
}
